import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';

const RequestForm = ({ onSubmit, customers, serviceTypes, products }) => {
  const [formData, setFormData] = useState({
    customerId: '',
    locationId: '',
    isNewCustomer: false,
    newCustomerName: '',
    newCustomerEmail: '',
    newCustomerPhone: '',
    newCustomerAddress: '',
    serviceType: '',
    selectedProducts: [],
    priority: '',
    description: '',
    urgentRequest: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [customerLocations, setCustomerLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  useEffect(() => {
    if (formData?.customerId && !formData?.isNewCustomer) {
      fetchLocationsForCustomer(formData?.customerId);
    } else {
      setCustomerLocations([]);
      setFormData(prev => ({ ...prev, locationId: '' }));
    }
  }, [formData?.customerId, formData?.isNewCustomer]);

  const fetchLocationsForCustomer = async (customerId) => {
    try {
      setLoadingLocations(true);

      // Guard: skip query if customerId is not a valid UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!customerId || !uuidRegex?.test(customerId)) {
        setCustomerLocations([]);
        setLoadingLocations(false);
        return;
      }

      const { data, error } = await supabase
        ?.from('customer_locations')
        ?.select('id, well_name, company, location, api')
        ?.eq('customer_id', customerId)
        ?.order('well_name');

      if (error) throw error;
      setCustomerLocations(data || []);
    } catch (error) {
      console.error('Error fetching locations:', error?.message);
      setCustomerLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low Priority', description: 'Standard service timeline' },
    { value: 'medium', label: 'Medium Priority', description: 'Expedited service' },
    { value: 'high', label: 'High Priority', description: 'Urgent attention required' },
    { value: 'critical', label: 'Critical', description: 'Immediate response needed' }
  ];

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'customerId':
        if (!formData?.isNewCustomer && !value) {
          error = 'Please select a customer';
        }
        break;
      case 'newCustomerName':
        if (formData?.isNewCustomer && !value?.trim()) {
          error = 'Customer name is required';
        }
        break;
      case 'newCustomerEmail':
        if (formData?.isNewCustomer) {
          if (!value?.trim()) {
            error = 'Email is required';
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) {
            error = 'Invalid email format (e.g., user@example.com)';
          }
        }
        break;
      case 'newCustomerPhone':
        if (formData?.isNewCustomer && !value?.trim()) {
          error = 'Phone number is required';
        }
        break;
      case 'serviceType':
        if (!value) {
          error = 'Service type is required';
        }
        break;
      case 'selectedProducts':
        if (value?.length === 0) {
          error = 'Please select at least one product or service';
        }
        break;
      case 'priority':
        if (!value) {
          error = 'Priority level is required';
        }
        break;
      case 'description':
        if (!value?.trim()) {
          error = 'Description is required';
        } else if (value?.trim()?.length < 20) {
          error = `Description must be at least 20 characters (${value?.trim()?.length}/20)`;
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = [
      'customerId',
      'newCustomerName',
      'newCustomerEmail',
      'newCustomerPhone',
      'serviceType',
      'selectedProducts',
      'priority',
      'description'
    ];

    fieldsToValidate?.forEach(field => {
      let error = validateField(field, formData?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData)?.forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    setTimeout(() => {
      onSubmit(formData);
      setFormData({
        customerId: '',
        locationId: '',
        isNewCustomer: false,
        newCustomerName: '',
        newCustomerEmail: '',
        newCustomerPhone: '',
        newCustomerAddress: '',
        serviceType: '',
        selectedProducts: [],
        priority: '',
        description: '',
        urgentRequest: false
      });
      setErrors({});
      setTouched({});
      setCustomerLocations([]);
      setIsSubmitting(false);
    }, 1500);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Real-time validation on change
    if (touched?.[field]) {
      let error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    let error = validateField(field, formData?.[field]);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const locationOptions = [
    { value: '', label: 'No specific location' },
    ...customerLocations?.map(loc => ({
      value: loc?.id,
      label: loc?.well_name,
      description: [loc?.company, loc?.location]?.filter(Boolean)?.join(' — ')
    }))
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-muted/30 rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="User" size={20} color="var(--color-primary)" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground">Customer Information</h3>
        </div>

        <Checkbox
          label="New Customer"
          description="Check this if the customer is not in the system"
          checked={formData?.isNewCustomer}
          onChange={(e) => handleInputChange('isNewCustomer', e?.target?.checked)}
          className="mb-6"
        />

        {!formData?.isNewCustomer ? (
          <div className="space-y-4">
            <Select
              label="Select Customer"
              placeholder="Choose a customer"
              description="Search by name or customer ID"
              error={errors?.customerId}
              required
              searchable
              options={customers}
              value={formData?.customerId}
              onChange={(value) => handleInputChange('customerId', value)}
            />
            {formData?.customerId && (
              <div>
                <Select
                  label="Location (Well)"
                  placeholder={loadingLocations ? 'Loading locations...' : 'Select a location (optional)'}
                  description="Link this request to a specific well or location"
                  options={locationOptions}
                  value={formData?.locationId}
                  onChange={(value) => handleInputChange('locationId', value)}
                  disabled={loadingLocations}
                />
                {formData?.locationId && customerLocations?.length > 0 && (() => {
                  const loc = customerLocations?.find(l => l?.id === formData?.locationId);
                  return loc ? (
                    <div className="mt-2 p-3 bg-warning/5 border border-warning/20 rounded-lg text-sm space-y-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="MapPin" size={14} color="var(--color-warning)" />
                        <span className="font-medium text-foreground">{loc?.well_name}</span>
                      </div>
                      {loc?.api && <p className="text-muted-foreground">API #: {loc?.api}</p>}
                      {loc?.company && <p className="text-muted-foreground">Company: {loc?.company}</p>}
                      {loc?.location && <p className="text-muted-foreground">Location: {loc?.location}</p>}
                    </div>
                  ) : null;
                })()}
                {customerLocations?.length === 0 && !loadingLocations && (
                  <p className="text-xs text-muted-foreground mt-1">No locations found for this customer.</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="Customer Name"
              type="text"
              placeholder="Enter full name"
              error={touched?.newCustomerName ? errors?.newCustomerName : ''}
              required
              value={formData?.newCustomerName}
              onChange={(e) => handleInputChange('newCustomerName', e?.target?.value)}
              onBlur={() => handleBlur('newCustomerName')}
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="customer@example.com"
              error={touched?.newCustomerEmail ? errors?.newCustomerEmail : ''}
              required
              value={formData?.newCustomerEmail}
              onChange={(e) => handleInputChange('newCustomerEmail', e?.target?.value)}
              onBlur={() => handleBlur('newCustomerEmail')}
            />
            <Input
              label="Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              error={touched?.newCustomerPhone ? errors?.newCustomerPhone : ''}
              required
              value={formData?.newCustomerPhone}
              onChange={(e) => handleInputChange('newCustomerPhone', e?.target?.value)}
              onBlur={() => handleBlur('newCustomerPhone')}
            />
            <Input
              label="Address"
              type="text"
              placeholder="Street address"
              description="Optional"
              value={formData?.newCustomerAddress}
              onChange={(e) => handleInputChange('newCustomerAddress', e?.target?.value)}
            />
          </div>
        )}
      </div>
      <div className="bg-muted/30 rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Wrench" size={20} color="var(--color-secondary)" />
          </div>
          <h3 className="text-lg font-heading font-semibold text-foreground">Service Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
          <Select
            label="Service Type"
            placeholder="Select service category"
            error={touched?.serviceType ? errors?.serviceType : ''}
            required
            options={serviceTypes}
            value={formData?.serviceType}
            onChange={(value) => {
              handleInputChange('serviceType', value);
              handleBlur('serviceType');
            }}
          />
          <Select
            label="Priority Level"
            placeholder="Set request priority"
            error={errors?.priority}
            required
            options={priorityOptions}
            value={formData?.priority}
            onChange={(value) => handleInputChange('priority', value)}
          />
        </div>

        <Select
          label="Products/Services Required"
          placeholder="Search and select items"
          description="Select all applicable products and services"
          error={errors?.selectedProducts}
          required
          multiple
          searchable
          clearable
          options={products}
          value={formData?.selectedProducts}
          onChange={(value) => handleInputChange('selectedProducts', value)}
          className="mb-6"
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Description <span className="text-error">*</span>
          </label>
          <textarea
            className="w-full min-h-[120px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y transition-smooth"
            placeholder="Provide detailed information about the service request, including any specific issues, requirements, or customer concerns..."
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
          />
          {errors?.description && (
            <p className="text-sm text-error flex items-center gap-1">
              <Icon name="AlertCircle" size={14} color="currentColor" />
              {errors?.description}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Minimum 20 characters required
          </p>
        </div>

        <Checkbox
          label="Mark as Urgent Request"
          description="This will trigger immediate manager notification"
          checked={formData?.urgentRequest}
          onChange={(e) => handleInputChange('urgentRequest', e?.target?.checked)}
          className="mt-6"
        />
      </div>
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <Button
          type="submit"
          variant="default"
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          className="flex-1"
        >
          {isSubmitting ? 'Submitting Request...' : 'Submit Request'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setFormData({
              customerId: '',
              locationId: '',
              isNewCustomer: false,
              newCustomerName: '',
              newCustomerEmail: '',
              newCustomerPhone: '',
              newCustomerAddress: '',
              serviceType: '',
              selectedProducts: [],
              priority: '',
              description: '',
              urgentRequest: false
            });
            setErrors({});
            setTouched({});
            setCustomerLocations([]);
          }}
          iconName="RotateCcw"
          iconPosition="left"
          className="flex-1 sm:flex-initial"
        >
          Reset Form
        </Button>
      </div>
    </form>
  );
};

export default RequestForm;